import React from 'react';
import { AlertTriangle, X } from 'lucide-react';

interface CustomConfirmProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmColor?: 'red' | 'green' | 'orange';
}

export const CustomConfirm: React.FC<CustomConfirmProps> = ({
  isOpen,
  title,
  message,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  confirmColor = 'red',
}) => {
  if (!isOpen) return null;

  const iconColorClasses = {
    red: 'bg-red-500/10 border-red-500/25 text-red-500',
    green: 'bg-green-500/10 border-green-500/25 text-green-500',
    orange: 'bg-orange-500/10 border-orange-500/25 text-orange-500',
  }[confirmColor];

  const buttonColorClasses = {
    red: 'bg-red-600 hover:bg-red-700 shadow-red-600/15',
    green: 'bg-green-600 hover:bg-green-700 shadow-green-600/15',
    orange: 'bg-orange-600 hover:bg-orange-700 shadow-orange-600/15',
  }[confirmColor];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Dark overlay backdrop */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={onCancel} />

      {/* Modal box */}
      <div className="relative bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-2xl w-full max-w-sm overflow-hidden transform transition-all p-5 space-y-4">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className={`p-2 border rounded-lg ${iconColorClasses}`}>
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="flex-1 space-y-1">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white tracking-wide">{title}</h3>
            <p className="text-xs text-gray-600 dark:text-gray-200 leading-normal">{message}</p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-200 dark:hover:text-white transition-colors cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Buttons */}
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-200 dark:border-gray-800/60">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-800 bg-gray-100 dark:bg-gray-950 text-xs font-bold text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white hover:border-gray-400 dark:hover:border-gray-500 transition-all cursor-pointer"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`px-3 py-1.5 rounded-lg active:scale-95 text-xs font-bold text-white transition-all cursor-pointer shadow-lg ${buttonColorClasses}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
